import { useState } from 'react'
import { useParams, Link } from '@tanstack/react-router'
import { useApp, useDeleteApp } from '@/hooks/useApps'
import { Button, SectionHeader, Skeleton, Empty, Badge, Card } from '@/components/ui'
import { ValuationPanel } from '@/components/ValuationPanel'
import { MarketingDeckPanel } from '@/components/MarketingDeckPanel'
import { AcquisitionPanel } from '@/components/AcquisitionPanel'
import { ImprovementsInline } from '@/components/ImprovementsInline'
import { AppHistoryPanel } from '@/components/AppHistoryPanel'
import { AppFormDialog } from '@/components/AppFormDialog'
import { ChevronLeft, Code2, ExternalLink, Pencil, Trash2, Folder, FileText } from 'lucide-react'
import { entityColor, statusColor, formatRelativeDate, formatDateLong, cn } from '@/lib/utils'
import { toast } from 'sonner'
import { useNavigate } from '@tanstack/react-router'

export function AppDetailPage() {
  const params = useParams({ from: '/apps/$slug' })
  const { data: app, isLoading } = useApp(params.slug)
  const [editing, setEditing] = useState(false)
  const del = useDeleteApp()
  const nav = useNavigate()

  if (isLoading) return <DetailSkeleton />
  if (!app) {
    return (
      <Empty
        icon={<Folder size={48} />}
        title="App not found"
        description={`No record for slug "${params.slug}"`}
        action={<Link to="/apps"><Button>Back to portfolio</Button></Link>}
      />
    )
  }

  const eC = entityColor(app.entity)
  const sC = statusColor(app.status)

  async function handleDelete() {
    if (!app?.id) return
    if (!confirm(`Delete ${app.name}? Audit log will retain a record.`)) return
    try {
      await del.mutateAsync(app.id)
      toast.success(`Deleted ${app.name}`)
      nav({ to: '/apps' })
    } catch (e) { toast.error((e as Error).message) }
  }

  return (
    <div className="space-y-8">
      <div>
        <Link to="/apps" className="inline-flex items-center gap-1 text-cream/55 hover:text-cream font-mono text-xs uppercase tracking-[0.12em] mb-4">
          <ChevronLeft size={14} /> Portfolio
        </Link>

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-6 border-b border-bronze/20">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className={cn('font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded border', eC.bg, eC.text, eC.border)}>
                {app.entity}
              </span>
              <Badge tone="cream">{app.category}</Badge>
              <span className={cn('inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.1em]', sC.text)}>
                <span className={cn('size-1 rounded-full', sC.dot)} />
                {app.status}
              </span>
              <Badge tone="bronze">{app.complexity}</Badge>
              <Badge tone="cream">{app.revenue_model_hint}</Badge>
            </div>
            <h1 className="font-display italic text-5xl text-cream font-light leading-none mb-3">{app.name}</h1>
            <p className="font-body text-cream/75 text-base max-w-3xl leading-relaxed">{app.description}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {app.github_repo && (
              <a href={app.github_repo} target="_blank" rel="noreferrer">
                <Button variant="ghost" size="sm"><Code2 size={14} /> Repo</Button>
              </a>
            )}
            {app.firebase_site && (
              <a href={`https://${app.firebase_site}.web.app`} target="_blank" rel="noreferrer">
                <Button variant="ghost" size="sm"><ExternalLink size={14} /> Live</Button>
              </a>
            )}
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)}><Pencil size={14} /> Edit</Button>
            <Button variant="danger" size="sm" onClick={handleDelete}><Trash2 size={14} /></Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
        <Stat label="Heat" value={app.score?.heat ?? 0} max={100} tone="gold" />
        <Stat label="Trend" value={app.score?.trend ?? 0} max={100} tone="bronze" />
        <Stat label="Defensibility" value={app.score?.defensibility ?? 0} max={100} tone="info" />
        <Stat label="AI Disruption Risk" value={app.score?.ai_disruption_risk ?? 0} max={100} tone="danger" inverse />
      </div>

      <ValuationPanel app={app} />

      <ImprovementsInline app={app} />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <MarketingDeckPanel app={app} />
        <AcquisitionPanel app={app} />
      </div>

      <AppHistoryPanel appSlug={app.slug} />

      <Card>
        <SectionHeader eyebrow="Tech profile" title="Stack & metadata" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="eyebrow mb-3">Stack</div>
            <div className="flex flex-wrap gap-1.5 mb-6">
              {app.stack?.map((s) => <Badge key={s} tone="bronze">{s}</Badge>)}
            </div>
            <div className="eyebrow mb-3">Key features</div>
            <ul className="space-y-1.5">
              {app.key_features?.map((f, i) => (
                <li key={i} className="font-body text-sm text-cream/80 flex gap-2">
                  <span className="text-gold-soft">·</span><span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="space-y-4">
            <Field icon={<Folder size={14} />} label="Path" value={<code className="font-mono text-xs text-cream/65 break-all">{app.path}</code>} />
            <Field icon={<Code2 size={14} />} label="LOC estimate" value={app.loc_estimate?.toLocaleString() ?? '—'} />
            <Field icon={<FileText size={14} />} label="Last modified" value={`${formatDateLong(app.last_modified)} (${formatRelativeDate(app.last_modified)})`} />
            {app.firebase_project && <Field label="Firebase project" value={<code className="font-mono text-xs">{app.firebase_project}</code>} />}
            {app.firebase_site && <Field label="Firebase site" value={<code className="font-mono text-xs">{app.firebase_site}</code>} />}
            {app.notes && (
              <div>
                <div className="eyebrow mb-2">Notes</div>
                <p className="font-body text-sm text-cream/75">{app.notes}</p>
              </div>
            )}
          </div>
        </div>
      </Card>

      <AppFormDialog open={editing} onClose={() => setEditing(false)} initial={app} />
    </div>
  )
}

function Stat({ label, value, max, tone, inverse = false }: {
  label: string; value: number; max: number; tone: string; inverse?: boolean
}) {
  const pct = Math.min(100, (value / max) * 100)
  const cls = {
    gold: 'text-gold-soft',
    bronze: 'text-bronze',
    info: 'text-info',
    danger: 'text-danger',
    success: 'text-success',
  }[tone] ?? 'text-cream'
  const barCls = {
    gold: 'bg-gold-soft',
    bronze: 'bg-bronze',
    info: 'bg-info',
    danger: 'bg-danger',
    success: 'bg-success',
  }[tone] ?? 'bg-cream'
  return (
    <div className="glass rounded-lg p-4">
      <div className={cn('eyebrow mb-2', cls)}>
        {label}{inverse && ' (lower is better)'}
      </div>
      <div className={cn('font-display italic text-4xl font-light leading-none mb-2', cls)}>{value}</div>
      <div className="h-1 rounded-full bg-cream/8 overflow-hidden">
        <div className={cn('h-full rounded-full transition-all', barCls)} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

function Field({ icon, label, value }: { icon?: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 eyebrow mb-1">
        {icon}<span>{label}</span>
      </div>
      <div className="text-cream font-body text-sm">{value}</div>
    </div>
  )
}

function DetailSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-32" />
      <div className="grid grid-cols-4 gap-4">
        {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-32" />)}
      </div>
      <Skeleton className="h-96" />
    </div>
  )
}
